/**
 * NotificationSettings Page - Configure alert preferences
 * Allows sellers to customize which alerts they receive and how
 * Enhanced with quiet hours and browser notification settings
 */

import { useState, useEffect } from 'react';
import {
  Bell,
  Eye,
  TrendingUp,
  Radio,
  Target,
  Calendar,
  Mail,
  Clock,
  Save,
  AlertCircle,
  Check,
  Moon,
  Volume2,
  Globe,
  MessageSquare
} from 'lucide-react';
import { useAlertPreferences, useAlertStats, useQuietHours } from '../../hooks/useAlerts';
import { requestNotificationPermission } from '../../utils/alertActions';
import { settingsAPI } from '../../services/api';

// Alert type settings configuration
const ALERT_TYPES = [
  {
    key: 'profile_view_alerts',
    thresholdKey: 'profile_view_threshold',
    icon: Eye,
    title: 'Profile Views',
    description: 'Get notified when buyers view your company profile multiple times',
    thresholdLabel: 'views in 24h',
    thresholdMin: 1,
    thresholdMax: 10
  },
  {
    key: 'interest_spike_alerts',
    thresholdKey: 'interest_spike_threshold',
    icon: TrendingUp,
    title: 'Interest Spikes',
    description: 'Get notified when buyer interest score increases significantly',
    thresholdLabel: 'point increase',
    thresholdMin: 10,
    thresholdMax: 50
  },
  {
    key: 'new_signal_alerts',
    thresholdKey: 'signal_strength_threshold',
    icon: Radio,
    title: 'New Signals',
    description: 'Get notified when new high-strength buyer signals are detected',
    thresholdLabel: 'min strength',
    thresholdMin: 5,
    thresholdMax: 10
  },
  {
    key: 'match_score_alerts',
    thresholdKey: 'match_score_threshold',
    icon: Target,
    title: 'Match Score Changes',
    description: 'Get notified when match scores cross threshold',
    thresholdLabel: 'min score',
    thresholdMin: 50,
    thresholdMax: 90
  },
  {
    key: 'meeting_request_alerts',
    icon: Calendar,
    title: 'Meeting Requests',
    description: 'Get notified when you receive new meeting requests',
    noThreshold: true
  }
];

export default function NotificationSettings() {
  const { preferences, loading, saving, error, updatePreferences } = useAlertPreferences();
  const { stats } = useAlertStats();
  const { isQuietHours } = useQuietHours();
  const [localPrefs, setLocalPrefs] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [browserNotificationStatus, setBrowserNotificationStatus] = useState('unknown');

  // Message notification settings (separate from alert preferences)
  const [messageSettings, setMessageSettings] = useState({
    email_on_new_message: true
  });
  const [messageSettingsLoading, setMessageSettingsLoading] = useState(true);

  // Check browser notification permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      setBrowserNotificationStatus(Notification.permission);
    } else {
      setBrowserNotificationStatus('unsupported');
    }
  }, []);

  // Load message notification settings
  useEffect(() => {
    async function loadMessageSettings() {
      try {
        const response = await settingsAPI.get();
        if (response.data?.data) {
          setMessageSettings(prev => ({
            ...prev,
            email_on_new_message: response.data.data.email_on_new_message ?? true
          }));
        }
      } catch (err) {
        console.error('Failed to load message settings:', err);
      } finally {
        setMessageSettingsLoading(false);
      }
    }
    loadMessageSettings();
  }, []);

  // Initialize local state when preferences load
  useEffect(() => {
    if (preferences) {
      setLocalPrefs({ ...preferences });
    }
  }, [preferences]);

  // Request browser notification permission
  const handleRequestPermission = async () => {
    const result = await requestNotificationPermission();
    setBrowserNotificationStatus(result);
  };

  // Handle toggle change
  const handleToggle = (key) => {
    setLocalPrefs(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    setSaveSuccess(false);
  };

  // Handle threshold change
  const handleThresholdChange = (key, value) => {
    setLocalPrefs(prev => ({
      ...prev,
      [key]: parseInt(value, 10)
    }));
    setSaveSuccess(false);
  };

  // Handle message settings toggle
  const handleMessageSettingsToggle = (key) => {
    setMessageSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    setSaveSuccess(false);
  };

  // Save preferences
  const handleSave = async () => {
    // Save alert preferences
    const success = await updatePreferences(localPrefs);

    // Also save message notification settings
    try {
      await settingsAPI.update(messageSettings);
    } catch (err) {
      console.error('Failed to save message settings:', err);
    }

    if (success) {
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!localPrefs) {
    return (
      <div className="text-center py-8 text-gray-500">
        Unable to load preferences
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Notification Settings</h1>
        <p className="mt-1 text-gray-500">
          Configure which alerts you receive and how they're delivered
        </p>
      </div>

      {/* Stats Summary */}
      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
            <div className="text-sm text-gray-500">Total Alerts</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-2xl font-bold text-purple-600">{stats.unread}</div>
            <div className="text-sm text-gray-500">Unread</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-2xl font-bold text-orange-600">{stats.highPriority}</div>
            <div className="text-sm text-gray-500">High Priority</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-2xl font-bold text-blue-600">{stats.today}</div>
            <div className="text-sm text-gray-500">Today</div>
          </div>
        </div>
      )}

      {/* Alert Types */}
      <div className="bg-white rounded-lg border border-gray-200 mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Alert Types
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Choose which types of alerts you want to receive
          </p>
        </div>

        <div className="divide-y divide-gray-100">
          {ALERT_TYPES.map((alertType) => {
            const IconComponent = alertType.icon;
            const isEnabled = localPrefs[alertType.key];

            return (
              <div key={alertType.key} className="px-6 py-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`p-2 rounded-lg ${isEnabled ? 'bg-purple-100' : 'bg-gray-100'}`}>
                      <IconComponent className={`w-5 h-5 ${isEnabled ? 'text-purple-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{alertType.title}</h3>
                      <p className="text-sm text-gray-500 mt-0.5">{alertType.description}</p>

                      {/* Threshold slider */}
                      {!alertType.noThreshold && isEnabled && (
                        <div className="mt-3 flex items-center gap-3">
                          <span className="text-sm text-gray-500">Threshold:</span>
                          <input
                            type="range"
                            min={alertType.thresholdMin}
                            max={alertType.thresholdMax}
                            value={localPrefs[alertType.thresholdKey] || alertType.thresholdMin}
                            onChange={(e) => handleThresholdChange(alertType.thresholdKey, e.target.value)}
                            className="w-32 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                          />
                          <span className="text-sm font-medium text-gray-900 min-w-[80px]">
                            {localPrefs[alertType.thresholdKey]} {alertType.thresholdLabel}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Toggle */}
                  <button
                    onClick={() => handleToggle(alertType.key)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                      isEnabled ? 'bg-purple-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        isEnabled ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Delivery Settings */}
      <div className="bg-white rounded-lg border border-gray-200 mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Delivery Settings
          </h2>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Email on new message */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MessageSquare className="w-5 h-5 text-gray-400" />
              <div>
                <h3 className="font-medium text-gray-900">New Message Emails</h3>
                <p className="text-sm text-gray-500">Receive email when a buyer sends you a message</p>
              </div>
            </div>
            <button
              onClick={() => handleMessageSettingsToggle('email_on_new_message')}
              disabled={messageSettingsLoading}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                messageSettings.email_on_new_message ? 'bg-purple-600' : 'bg-gray-200'
              } ${messageSettingsLoading ? 'opacity-50' : ''}`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  messageSettings.email_on_new_message ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {/* In-app notifications */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-gray-900">In-App Notifications</h3>
              <p className="text-sm text-gray-500">Show notifications in the app</p>
            </div>
            <button
              onClick={() => handleToggle('in_app_notifications')}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                localPrefs.in_app_notifications ? 'bg-purple-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  localPrefs.in_app_notifications ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {/* Email notifications */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-gray-900">Email Notifications</h3>
              <p className="text-sm text-gray-500">Receive alerts via email</p>
            </div>
            <button
              onClick={() => handleToggle('email_notifications')}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                localPrefs.email_notifications ? 'bg-purple-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  localPrefs.email_notifications ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {/* Email frequency */}
          {localPrefs.email_notifications && (
            <div className="pl-4 border-l-2 border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Frequency
              </label>
              <select
                value={localPrefs.email_digest_frequency || 'daily'}
                onChange={(e) => handleThresholdChange('email_digest_frequency', e.target.value)}
                className="block w-full max-w-xs rounded-lg border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
              >
                <option value="realtime">Real-time</option>
                <option value="daily">Daily digest</option>
                <option value="weekly">Weekly digest</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Rate Limiting */}
      <div className="bg-white rounded-lg border border-gray-200 mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Rate Limiting
          </h2>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Maximum alerts per day
            </label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="10"
                max="100"
                step="10"
                value={localPrefs.max_alerts_per_day || 50}
                onChange={(e) => handleThresholdChange('max_alerts_per_day', e.target.value)}
                className="w-48 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
              />
              <span className="text-sm font-medium text-gray-900 min-w-[60px]">
                {localPrefs.max_alerts_per_day} alerts
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Minimum interval between similar alerts
            </label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="15"
                max="120"
                step="15"
                value={localPrefs.min_alert_interval_minutes || 30}
                onChange={(e) => handleThresholdChange('min_alert_interval_minutes', e.target.value)}
                className="w-48 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
              />
              <span className="text-sm font-medium text-gray-900 min-w-[80px]">
                {localPrefs.min_alert_interval_minutes} minutes
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Quiet Hours */}
      <div className="bg-white rounded-lg border border-gray-200 mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Moon className="w-5 h-5" />
            Quiet Hours
            {isQuietHours && (
              <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-full">
                Active Now
              </span>
            )}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Pause notifications during specific hours
          </p>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Enable quiet hours */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-gray-900">Enable Quiet Hours</h3>
              <p className="text-sm text-gray-500">Mute notifications during set hours</p>
            </div>
            <button
              onClick={() => handleToggle('quiet_hours_enabled')}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                localPrefs.quiet_hours_enabled ? 'bg-purple-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  localPrefs.quiet_hours_enabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {/* Time range */}
          {localPrefs.quiet_hours_enabled && (
            <div className="pl-4 border-l-2 border-gray-200 space-y-3">
              <div className="flex items-center gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                  <input
                    type="time"
                    value={localPrefs.quiet_hours_start || '22:00'}
                    onChange={(e) => setLocalPrefs(prev => ({ ...prev, quiet_hours_start: e.target.value }))}
                    className="block rounded-lg border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                  <input
                    type="time"
                    value={localPrefs.quiet_hours_end || '08:00'}
                    onChange={(e) => setLocalPrefs(prev => ({ ...prev, quiet_hours_end: e.target.value }))}
                    className="block rounded-lg border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Globe className="w-4 h-4 inline mr-1" />
                  Timezone
                </label>
                <select
                  value={localPrefs.timezone || 'Asia/Shanghai'}
                  onChange={(e) => setLocalPrefs(prev => ({ ...prev, timezone: e.target.value }))}
                  className="block w-full max-w-xs rounded-lg border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                >
                  <option value="Asia/Shanghai">China (CST)</option>
                  <option value="America/New_York">US Eastern</option>
                  <option value="America/Los_Angeles">US Pacific</option>
                  <option value="Europe/London">UK (GMT/BST)</option>
                  <option value="Europe/Paris">Central Europe</option>
                  <option value="Asia/Tokyo">Japan (JST)</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Browser Notifications */}
      <div className="bg-white rounded-lg border border-gray-200 mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Volume2 className="w-5 h-5" />
            Browser Notifications
          </h2>
        </div>

        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-gray-900">Desktop Notifications</h3>
              <p className="text-sm text-gray-500">
                {browserNotificationStatus === 'granted' && 'Notifications are enabled'}
                {browserNotificationStatus === 'denied' && 'Notifications are blocked in browser settings'}
                {browserNotificationStatus === 'default' && 'Click to enable desktop notifications'}
                {browserNotificationStatus === 'unsupported' && 'Your browser does not support notifications'}
              </p>
            </div>
            {browserNotificationStatus === 'default' && (
              <button
                onClick={handleRequestPermission}
                className="px-4 py-2 text-sm font-medium text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
              >
                Enable
              </button>
            )}
            {browserNotificationStatus === 'granted' && (
              <span className="flex items-center gap-1 text-green-600">
                <Check className="w-4 h-4" />
                Enabled
              </span>
            )}
            {browserNotificationStatus === 'denied' && (
              <span className="text-sm text-gray-500">
                Check browser settings
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      {/* Save button */}
      <div className="flex items-center justify-end gap-4">
        {saveSuccess && (
          <span className="text-green-600 flex items-center gap-1">
            <Check className="w-4 h-4" />
            Saved successfully
          </span>
        )}
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  );
}
